import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Heading,
  Container,
  Center,
  Icon,
  InputGroup,
  InputLeftElement,
  Image,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { FaFlask, FaKey, FaCheckCircle, FaEnvelope } from 'react-icons/fa';
import OnboardingGuide from './OnboardingGuide';
import { checkLockout, recordFailedAttempt, clearAttempts } from '../../utils/loginRateLimiter';

const LabPortalLogin = () => {
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [labData, setLabData] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [authenticatedLab, setAuthenticatedLab] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a new signup
  const isNewSignup = location.state?.isNewSignup;
  const signupEmail = location.state?.email;
  const labName = location.state?.labName;
  const planName = location.state?.plan;
  const newSignupSecretKey = location.state?.secretKey;
  const forceShowOnboarding = location.state?.showOnboarding;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Check lockout before attempting login
    const lockout = checkLockout('lab');
    if (lockout.locked) {
      toast({
        title: 'Account Locked',
        description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      let labData = null;
      let secretKeyInfo = null;
      let userName = null;
      
      // First, try to find in main labs.secret_key
      const { data: mainLabData, error: mainError } = await supabase
        .from('labs')
        .select('*')
        .eq('secret_key', secretKey)
        .eq('is_active', true)
        .maybeSingle();

      if (mainLabData && !mainError) {
        // Main secret key found
        labData = mainLabData;
        userName = 'Admin'; // Main key is admin
      } else {
        // Check lab_secret_keys table
        const { data: secretKeyData, error: keyError } = await supabase
          .from('lab_secret_keys')
          .select('id, lab_id, user_name, is_active')
          .eq('secret_key', secretKey)
          .eq('is_active', true)
          .single();

        if (secretKeyData && !keyError) {
          // Fetch the associated lab
          const { data: matchedLab, error: labFetchError } = await supabase
            .from('labs')
            .select('*')
            .eq('id', secretKeyData.lab_id)
            .eq('is_active', true)
            .maybeSingle();

          if (matchedLab && !labFetchError) {
            // Sub-user secret key found
            secretKeyInfo = secretKeyData;
            labData = matchedLab;
            userName = secretKeyData.user_name;

            // Update last_used_at for this secret key
            await supabase
              .from('lab_secret_keys')
              .update({ last_used_at: new Date().toISOString() })
              .eq('id', secretKeyData.id);
          }
        }
      }

      if (!labData) {
        const result = recordFailedAttempt('lab');
        toast({
          title: 'Invalid Secret Key',
          description: result.locked
            ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
            : `The secret key is invalid or the lab is inactive. ${result.attemptsRemaining} attempts remaining.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      clearAttempts('lab');

      // Record successful login
      const now = new Date().toISOString();
      try {
        // Log the login activity
        await supabase.from('lab_login_log').insert([{
          lab_id: labData.id,
          login_timestamp: now,
          ip_address: null, // Could be enhanced to capture real IP
          user_agent: navigator.userAgent,
          success: true,
          user_name: userName // Track which user logged in
        }]);

        // Update last_login_at in labs table
        await supabase
          .from('labs')
          .update({ last_login_at: now })
          .eq('id', labData.id);
      } catch (logError) {
        // Don't fail login if logging fails
        console.warn('Failed to log login activity:', logError);
      }

      // Store lab session data — never persist secret_key
      const labSession = {
        id: labData.id,
        name: labData.name,
        logo_url: labData.logo_url,
        user_name: userName, // Store which user is logged in
        is_main_key: !secretKeyInfo, // Track if this is the main key or sub-key
        loginTime: now,
      };

      localStorage.setItem('labSession', JSON.stringify(labSession));

      // Check if lab should see onboarding guide
      const neverShowOnboarding = localStorage.getItem(`onboarding-never-show-${labData.name}`);
      const hasCompletedOnboarding = localStorage.getItem(`onboarding-completed-${labData.name}`);
      
      // Show onboarding if:
      // 1. User hasn't opted out AND
      // 2. (This is a new signup OR lab has never completed onboarding OR forced to show)
      const shouldShowOnboarding = (
        neverShowOnboarding !== 'true' && 
        (isNewSignup || hasCompletedOnboarding !== 'true' || forceShowOnboarding)
      );

      if (shouldShowOnboarding) {
        // Show onboarding guide
        setAuthenticatedLab(labData);
        setShowOnboarding(true);
        
        const welcomeMessage = isNewSignup 
          ? `Let's get you started with a quick tour, ${labData.name}`
          : `Welcome to your portal! Let's show you around, ${labData.name}`;
        
        toast({
          title: isNewSignup ? 'Welcome to Your Portal!' : 'Portal Tour Available',
          description: welcomeMessage,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Login Successful',
          description: `Welcome back to ${labData.name} portal${userName && userName !== 'Admin' ? `, ${userName}` : ''}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Redirect to lab portal dashboard
        navigate('/lab-portal/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Login Error',
        description: `An unexpected error occurred during login: ${error?.message ?? 'unknown error'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if we have a lab session to show the lab's logo
  React.useEffect(() => {
    const checkSession = () => {
      try {
        const session = localStorage.getItem('labSession');
        if (session) {
          const labSession = JSON.parse(session);
          const loginTime = new Date(labSession.loginTime);
          const now = new Date();
          const hoursDiff = (now - loginTime) / (1000 * 60 * 60);
          
          if (hoursDiff < 24) {
            // Valid session, redirect to dashboard
            navigate('/lab-portal/dashboard');
            return;
          } else {
            // Expired session, clear it
            localStorage.removeItem('labSession');
          }
        }
      } catch (error) {
        localStorage.removeItem('labSession');
      }
    };
    
    checkSession();
  }, [navigate]);

  // Auto-login for new signups
  React.useEffect(() => {
    const autoLoginNewSignup = async () => {
      if (isNewSignup && newSignupSecretKey && !localStorage.getItem('labSession')) {
        try {
          // Fetch lab data using the secret key
          const { data, error } = await supabase
            .from('labs')
            .select('*')
            .eq('secret_key', newSignupSecretKey)
            .eq('is_active', true)
            .maybeSingle();

          if (error || !data) {
            console.error('Auto-login failed:', error);
            return;
          }

          // Create lab session — never persist secret_key
          const now = new Date();
          const labSession = {
            id: data.id,
            name: data.name,
            logo_url: data.logo_url,
            is_main_key: true,
            loginTime: now,
          };

          localStorage.setItem('labSession', JSON.stringify(labSession));
          
          // Set up for onboarding
          setAuthenticatedLab(data);
          setShowOnboarding(true);
          
          toast({
            title: 'Welcome to Your Portal!',
            description: `Successfully set up ${data.name} lab portal!`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          
        } catch (error) {
          console.error('Auto-login error:', error);
        }
      }
    };

    autoLoginNewSignup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewSignup, newSignupSecretKey]);

  // Try to identify lab by secret key for branding (without authenticating)
  const identifyLab = async (key) => {
    if (key.length > 3) {
      try {
        const { data } = await supabase
          .from('labs')
          .select('name, logo_url')
          .eq('secret_key', key)
          .eq('is_active', true)
          .maybeSingle();
        
        if (data) {
          setLabData(data);
        } else {
          setLabData(null);
        }
      } catch {
        setLabData(null);
      }
    } else {
      setLabData(null);
    }
  };

  const handleSecretKeyChange = (e) => {
    const value = e.target.value;
    setSecretKey(value);
    identifyLab(value);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    navigate('/lab-portal/dashboard');
  };

  // Show onboarding guide for new signups after authentication
  if (showOnboarding && authenticatedLab) {
    return (
      <Box minH="100vh" bg="gray.50">
        <OnboardingGuide 
          onComplete={handleOnboardingComplete}
          labName={authenticatedLab.name}
        />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="md">
        {/* New Signup Welcome Banner */}
        {isNewSignup && (
          <Alert status="success" mb={6} borderRadius="lg" bg="green.50" border="1px" borderColor="green.200">
            <AlertIcon as={FaCheckCircle} color="green.500" />
            <Box>
              <AlertTitle color="green.800">Welcome to Your Lab Portal!</AlertTitle>
              <AlertDescription color="green.700">
                <VStack align="start" spacing={2} mt={2}>
                  <Text fontSize="sm">
                    🎉 <strong>{labName}</strong> has successfully joined our network with the <strong>{planName}</strong> plan!
                  </Text>
                  <Text fontSize="sm">
                    📧 We've sent your login credentials to <strong>{signupEmail}</strong>
                  </Text>
                  {newSignupSecretKey && (
                    <Box bg="blue.50" p={3} borderRadius="md" border="1px" borderColor="blue.200">
                      <Text fontSize="sm" mb={2}>
                        <strong>🔑 Your Secret Key (Development):</strong>
                      </Text>
                      <Box display="flex" alignItems="center" gap={2}>
                        <code style={{fontSize: '14px', fontWeight: 'bold', color: '#2B6CB0', background: 'white', padding: '4px 8px', borderRadius: '4px', border: '1px solid #E2E8F0'}}>{newSignupSecretKey}</code>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          onClick={() => {
                            navigator.clipboard.writeText(newSignupSecretKey);
                            toast({
                              title: 'Copied!',
                              description: 'Secret key copied to clipboard',
                              status: 'success',
                              duration: 2000,
                              isClosable: true,
                            });
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Box>
                  )}
                  <Text fontSize="sm">
                    🔑 Enter your secret key below to access your portal.
                  </Text>
                </VStack>
              </AlertDescription>
            </Box>
          </Alert>
        )}
        
        <Center mb={8}>
          <VStack spacing={4}>
            <Box p={4} bg="blue.500" borderRadius="full">
              {labData?.logo_url ? (
                <Image 
                  src={labData.logo_url} 
                  alt={`${labData.name} logo`}
                  boxSize="64px"
                  objectFit="contain"
                  bg="white"
                  borderRadius="full"
                  p={2}
                />
              ) : (
                <Icon as={FaFlask} boxSize={8} color="white" />
              )}
            </Box>
            <VStack spacing={2} textAlign="center">
              <Heading size="lg" color="gray.700">
                {labData ? `${labData.name} Portal` : 'Lab Portal Login'}
              </Heading>
              <Text color="gray.600" fontSize="md">
                Enter your lab's secret key to access the portal
              </Text>
            </VStack>
          </VStack>
        </Center>

        <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
          <form onSubmit={handleLogin}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>Secret Key</FormLabel>
                <InputGroup>
                  <InputLeftElement>
                    <Icon as={FaKey} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="password"
                    value={secretKey}
                    onChange={handleSecretKeyChange}
                    placeholder="Enter your lab's secret key"
                    size="lg"
                  />
                </InputGroup>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  {isNewSignup 
                    ? "Your secret key has been sent to your email. It looks like: LAB-XXXX-XXXX-XXXX" 
                    : "Contact your system administrator if you don't have your secret key."
                  }
                </Text>
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                width="100%"
                isLoading={loading}
                loadingText="Authenticating..."
              >
                Access Portal
              </Button>
            </VStack>
          </form>
        </Box>

        <Center mt={6}>
          <Text fontSize="sm" color="gray.500">
            This is a secure portal for laboratory partners only.
          </Text>
        </Center>
      </Container>
    </Box>
  );
};

export default LabPortalLogin; 